export const pricing = {
    currency: "EUR",
    batchMicroEurPerSecond: 200,
    batchEurPerMinute: 0.012,
    realtimeEurPerMinute: 0.016,
    /**
     * Burned-in caption renders (spec §11, "Render pricing (modular)"). Every knob is here: `enabled: false`
     * makes all renders free; `freePerAccount` renders per account are free; after that each started second of
     * source media costs `microEurPerSecond` (€0.01 per minute = 166.67 µ€/s, rounded up to 167).
     */
    render: {
        enabled: true,
        freePerAccount: 2,
        eurPerMinute: 0.01,
        microEurPerSecond: 167,
        maxActive: 2,
        maxSourceSeconds: 1800,
    },
    signupGrantSeconds: 900,
    trialMaxSeconds: 60,
    trialPerIpPerDay: 3,
    trialGlobalPerDay: 500,
    minTopupEur: 10,
    maxTopupEur: 1000,
    maxUploadBytes: 2 * 1024 ** 3,
    trialMaxUploadBytes: 100 * 1024 ** 2,
    maxDurationSeconds: 14400,
    apiRequestsPerMinute: 60,
    competitorReference: {
        googleChirp3UsdPerMinute: 0.016,
        checked: "2026-11-02",
        source: "https://cloud.google.com/speech-to-text/pricing",
    },
};
/**
 * Abuse limits for the free tiers (spec §7 "Abuse limits"). They sit next to `pricing.trialPerIpPerDay`, which
 * stays the per-address limit, and every value is a default the operator may tune here.
 * Trial limits count rows in `trials`, which retention removes after 24 h, so a rolling 24 h window is the
 * longest one a trial limit can have. Signup-grant limits count verified accounts.
 */
// Mobile carriers in Albania and Kosovo put many real users behind shared addresses (CGNAT), and a
// few ISPs carry most traffic, so the network and ASN caps only stop bulk abuse. The inbox rule,
// the disposable-domain block and Turnstile do the per-person work.
export const abuseLimits = {
    trial: {
        /** Trials per IPv4 /24 or IPv6 /48 in a rolling 24 h. Wider than one address: a home or office shares a prefix. */
        perNetworkPerDay: 10,
        /** Trials per autonomous system (ASN) per UTC day. A last-resort cap for a whole provider. */
        perAsnPerDay: 100,
        /** Trials per first-party device token (cookie `fj_dev` or the widget's stored copy) in a rolling 24 h. */
        perDevicePerDay: 3,
    },
    signupGrant: {
        /** Signup grants per IPv4 /24 or IPv6 /48 (the network the account signed up from) in a rolling 7 days. */
        perNetworkPerWeek: 10,
        /** Signup grants per ASN per UTC day. */
        perAsnPerDay: 60,
        /** Signup grants per first-party device id (the `X-Fjalim-Device` the sign-up form sent) in a rolling 30 days. */
        perDevicePerMonth: 2,
    },
};
export const retention = { hotDays: 90, totalDays: 365, trialHours: 24 };
export const models = {
    fast: { id: "fast", eurPerMinute: 0.012, microEurPerSecond: 200, available: true },
    accurate: { id: "accurate", eurPerMinute: 0.024, microEurPerSecond: 400, available: false },
};
/**
 * Add-ons are charged on top of the model price, per started second, in a separate `usage_addon`
 * ledger row (spec §12). Speaker labels are €0.002 per minute, which is 33.33 µ€ per second;
 * billing is in whole µ€, so it is rounded up to 34 µ€ per second (2,040 µ€ = €0.00204 per minute,
 * +2%). Rounding up never undercharges. Sold today, so a speakers job is 200 + 34 = 234 µ€ per second.
 */
export const addons = {
    speakers: { id: "speakers", eurPerMinute: 0.002, microEurPerSecond: 34, available: true },
};
/** Lowest per-minute price of a model sold today: the "from €0.012/min" price. */
export const fromEurPerMinute = Math.min(...Object.values(models)
    .filter((m) => m.available)
    .map((m) => m.eurPerMinute));
/**
 * Quote for `seconds` of audio in integer micro-euros: each started second costs the model price
 * plus every distinct add-on. Planned (unavailable) options can be quoted but are not billable;
 * real billing stays on `costMicroEur`. Invalid or non-positive durations cost 0.
 */
export function estimateMicroEur(seconds, options) {
    if (!Number.isFinite(seconds) || seconds <= 0)
        return 0;
    const perSecond = models[options.model].microEurPerSecond +
        [...new Set(options.addons ?? [])].reduce((sum, id) => sum + addons[id].microEurPerSecond, 0);
    return Math.ceil(seconds) * perSecond;
}
/** Batch cost in integer micro-euros, billed per started second. */
export function costMicroEur(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0)
        return 0;
    return Math.ceil(seconds) * pricing.batchMicroEurPerSecond;
}
/**
 * Price of one paid render of `seconds` of source media in integer micro-euros: each started second costs
 * `pricing.render.microEurPerSecond`. Whether a render is free is decided by the API (`enabled`,
 * `freePerAccount`); this is the price when it is not. Invalid or non-positive durations cost 0.
 */
export function renderCostMicroEur(seconds) {
    if (!Number.isFinite(seconds) || seconds <= 0)
        return 0;
    return Math.ceil(seconds) * pricing.render.microEurPerSecond;
}
/** Format micro-euros as euros. Amounts under €1 show up to 4 decimals (12000 µ€ → €0.012). */
export function formatEur(microEur, locale) {
    const eur = microEur / 1_000_000;
    const small = Math.abs(eur) < 1;
    return new Intl.NumberFormat(locale === "sq" ? "sq-AL" : "en-IE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: small ? 4 : 2,
    }).format(eur);
}
